import React from "react";
import Tooltip from "@mui/material/Tooltip";
import { formatNumber } from "../../helpers/formatter";
import { formatKpiPercent } from "../../helpers/scoreAggregate";
import { rawTargetOf } from "../../helpers/kpiTargetScale";
import { isKpiGamified, resolveGamePointsRule, getAdvancedGameSettingsFromKpi } from "../../constants/kpi";

/**
 * The "how does this metric work" tooltip that sits beside a metric's title on
 * the dashboard tiles.
 *
 * It is written FOR THE PLAYER: how they earn points and what they're aiming
 * for. Every line is derived from what the admin already configured on the
 * metric, so the tooltip and the gamification settings can never disagree.
 *
 * Rendered through MUI's Tooltip so it portals out of the tile (the tile
 * clips its own overflow) and handles flipping, touch and keyboard for free.
 */

const FREQ_LABEL = { weekly: "a week", monthly: "a month", daily: "a day" };

/**
 * Build the "how you earn points" / "your goal" text for ONE metric
 * definition (either the card's own blended `kpi`, or a single workspace's
 * own copy of it when workspaces disagree on the rule — see
 * `rulesDiffer` below). `target` is passed in separately since the combined
 * card and a raw per-workspace def resolve their daily target differently.
 */
function buildEarnGoal(def, target, fmt) {
  const scores = isKpiGamified(def);
  if (!scores) return { scores, earnText: null, goalText: null };

  const advanced = getAdvancedGameSettingsFromKpi(def);
  const { metricAmount, pointsEarned } = resolveGamePointsRule(def, advanced);
  const freq = FREQ_LABEL[advanced.resetFrequency] || "a day";
  const lowerIsBetter = (def.direction ?? 1) < 0;
  const opt = def.optional || {};
  const gameConfig = def.gameConfig || {};

  // ---- how points are earned ------------------------------------------
  let earnText;
  if (def.metricType === "anchor") {
    const above = Number(opt.aboveBenchmark ?? 0);
    const below = Number(opt.belowBenchmark ?? 0);
    const step = fmt(opt.unit ?? 1);
    earnText = (
      <>
        Benchmark is <b>{fmt(target)}</b>. You gain <span className="mi-pts">{above} pts</span> for
        every {step} above it{below ? <> — and lose {below} for every {step} below</> : null}.
      </>
    );
  } else if (advanced.monitorMode) {
    earnText = <>This one is watched, not scored — it earns <b>no points</b>.</>;
  } else {
    // Admins often configure fractional points (1 unit = 0.1 pts). "0.1
    // points" is meaningless to a player, so scale the rule up to whole
    // points: 1 = 0.1pt becomes "Every 10 = 1 point".
    let amt = Number(metricAmount) || 1;
    let pts = Number(pointsEarned) || 0;
    if (pts > 0 && pts < 1) {
      amt /= pts;
      pts = 1;
    }
    pts = Math.round(pts * 100) / 100;
    earnText = (
      <>
        Every <b>{fmt(amt)}</b> ={" "}
        <span className="mi-pts">
          {pts} point{pts === 1 ? "" : "s"}
        </span>
      </>
    );
  }

  // ---- the goal --------------------------------------------------------
  const goalOverride = (gameConfig.dailyGoalText ?? def.dailyGoalText ?? "").trim();
  let goalText;
  if (goalOverride) {
    goalText = goalOverride;
  } else if (def.metricType === "anchor") {
    goalText = lowerIsBetter ? (
      <>Stay under <b>{fmt(target)}</b></>
    ) : (
      <>Stay above <b>{fmt(target)}</b></>
    );
  } else {
    goalText = lowerIsBetter ? (
      <>Keep it under <b>{fmt(target)}</b> {freq} — lower is better</>
    ) : (
      <>
        <b>{fmt(target)}</b> {freq}
      </>
    );
  }

  return { scores, earnText, goalText };
}

/* Whether two workspaces' own copies of this metric would tell the player a
   different story. Mirrors buildEarnGoal's own branches exactly (anchor vs
   monitor vs standard) instead of diffing the whole raw def — comparing
   fields buildEarnGoal never reads (e.g. a leftover pointScale on an anchor
   metric) produced false splits: two workspaces with an IDENTICAL "Benchmark
   is 50%... Stay above 50%" rule still rendered as two separate blocks. */
function ruleSignature(def) {
  const opt = def?.optional || {};
  const params = def?.parameters || {};
  const gameConfig = def?.gameConfig || {};
  const goalOverride = (gameConfig.dailyGoalText ?? def?.dailyGoalText ?? "").trim();
  const direction = def?.direction ?? 1;
  const target = rawTargetOf(def);

  if (def?.metricType === "anchor") {
    return JSON.stringify([
      "anchor", direction, target,
      Number(opt.aboveBenchmark ?? 0),
      Number(opt.belowBenchmark ?? 0),
      opt.unit ?? 1,
      goalOverride,
    ]);
  }
  if (params.monitorMode) {
    return JSON.stringify(["monitor"]);
  }
  return JSON.stringify([
    "standard", direction, target,
    def?.pointValue ?? null,
    def?.pointScale ?? null,
    params.pointsRule?.metricAmount ?? null,
    params.pointsRule?.pointsEarned ?? null,
    params.resetFrequency || "daily",
    goalOverride,
  ]);
}

export default function MetricInfoTooltip({ kpi, darkMode = false }) {
  if (!kpi) return null;

  const kind = kpi.kind ?? kpi.model?.kind;
  const fmt = (val) => {
    if (kind === "%") return formatKpiPercent(kpi, Number(val) || 0);
    return formatNumber(Math.round(Number(val) || 0), kind);
  };

  // Combined-card context: which workspaces feed this tile, and whether they
  // each configured the metric's rule the same way. `_perWorkspace` /
  // `_workspaceCount` are stamped by useScopedMetrics for cards scoped to 2+
  // workspaces (Team Performance vs. The Yeti's "Automatic"/"Custom" combine).
  const perWorkspace = Array.isArray(kpi._perWorkspace) ? kpi._perWorkspace : [];
  const workspaceCount = kpi._workspaceCount || perWorkspace.length;
  const isCombined = workspaceCount >= 2 && perWorkspace.length >= 2;
  const rulesDiffer = isCombined
    && new Set(perWorkspace.map((w) => ruleSignature(w.def))).size > 1;

  const { scores, earnText, goalText } = buildEarnGoal(kpi, kpi.target, fmt);

  const perWorkspaceRows = rulesDiffer
    ? perWorkspace.map((w) => ({
      ...w,
      ...buildEarnGoal(w.def, rawTargetOf(w.def), fmt),
    }))
    : [];

  // The admin's own guide line (the grey pill on the TV board).
  const guide = (kpi.gameConfig?.guideText ?? kpi.guideText ?? "").trim();

  let mainBody;
  if (!scores) {
    mainBody = (
      <div className="mi-row">
        <span className="mi-ico">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="4" /><circle cx="12" cy="12" r="1" fill="currentColor" /></svg>
        </span>
        <span>
          <span className="mi-lbl">How it works</span>
          <span className="mi-val">
            This one is <b>tracked only</b> — it doesn&apos;t earn you points.
          </span>
        </span>
      </div>
    );
  } else if (rulesDiffer) {
    // Workspaces disagree on the benchmark/points rule — speaking for only
    // one of them would tell some players the wrong goal, so show each
    // workspace's own rule instead of a single blended line.
    mainBody = perWorkspaceRows.map((w) => (
      <div className="mi-ws-block" key={w.workspaceId}>
        <div className="mi-ws-name">{w.workspaceName}</div>
        <div className="mi-row">
          <span className="mi-ico">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2 4.1 12.6a1 1 0 0 0 .8 1.6H11l-1 8 8.9-10.6a1 1 0 0 0-.8-1.6H12z" /></svg>
          </span>
          <span>
            <span className="mi-lbl">How you earn points</span>
            <span className="mi-val">{w.earnText}</span>
          </span>
        </div>
        <div className="mi-row">
          <span className="mi-ico">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="4" /><circle cx="12" cy="12" r="1" fill="currentColor" /></svg>
          </span>
          <span>
            <span className="mi-lbl">Your goal</span>
            <span className="mi-val">{w.goalText}</span>
          </span>
        </div>
      </div>
    ));
  } else {
    mainBody = (
      <>
        <div className="mi-row">
          <span className="mi-ico">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2 4.1 12.6a1 1 0 0 0 .8 1.6H11l-1 8 8.9-10.6a1 1 0 0 0-.8-1.6H12z" /></svg>
          </span>
          <span>
            <span className="mi-lbl">How you earn points</span>
            <span className="mi-val">{earnText}</span>
          </span>
        </div>
        <div className="mi-row">
          <span className="mi-ico">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="4" /><circle cx="12" cy="12" r="1" fill="currentColor" /></svg>
          </span>
          <span>
            <span className="mi-lbl">Your goal</span>
            <span className="mi-val">{goalText}</span>
          </span>
        </div>
      </>
    );
  }

  const content = (
    <div className="mi-tip">
      <div className="mi-head">
        <span className="mi-name">{kpi.title || kpi.name}</span>
        <span className={`mi-badge${scores ? "" : " plain"}`}>{scores ? "Earns points" : "No points"}</span>
      </div>

      {isCombined && (
        <div className="mi-combined">
          Combined from {workspaceCount} workspaces
          {perWorkspace.length ? `: ${perWorkspace.map((w) => w.workspaceName).join(" + ")}` : ""}
        </div>
      )}

      {mainBody}

      {/* The admin's own guide line (the grey pill on the TV board) — shown
          verbatim as a footnote so their wording still reaches the player. */}
      {guide ? <div className="mi-note">{guide}</div> : null}
    </div>
  );

  return (
    <Tooltip
      title={content}
      placement="bottom-start"
      enterTouchDelay={0}
      leaveTouchDelay={6000}
      componentsProps={{
        tooltip: {
          sx: {
            p: 0,
            maxWidth: "none",
            bgcolor: "transparent",
            "& .mi-tip": {
              width: 280,
              background: "#FFFFFF",
              // A real hairline, not the near-invisible white one: the tooltip
              // lands on white cards, so it needs its own defined edge. The
              // shadow does the lifting; the border only draws the outline.
              border: "1px solid #E4E9F0",
              borderRadius: "12px",
              boxShadow: "0 12px 28px -10px rgba(16,24,40,0.16), 0 2px 6px -2px rgba(16,24,40,0.08)",
              padding: "12px 13px",
              textAlign: "left",
            },
            "& .mi-head": {
              display: "flex", alignItems: "center", gap: "7px",
              paddingBottom: "9px", borderBottom: "1px solid #F0F2F6",
            },
            "& .mi-name": {
              fontFamily: "DM Sans", fontSize: "12.5px", fontWeight: 600, color: "#101828",
              flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            },
            "& .mi-badge": {
              fontFamily: "DM Sans", fontSize: "9.5px", fontWeight: 700, letterSpacing: "0.04em",
              textTransform: "uppercase", padding: "2px 6px", borderRadius: "999px",
              background: "var(--gmb-primary-soft, #EAF1F8)", color: "var(--gmb-primary, #2C6BA1)", flex: "none",
            },
            "& .mi-badge.plain": { background: "#F2F4F7", color: "#667085" },
            // "Combined from N workspaces" indicator — sits under the header,
            // above whichever earn/goal content follows (single or per-ws).
            "& .mi-combined": {
              display: "flex", alignItems: "center", gap: "6px",
              marginTop: "9px", padding: "5px 8px",
              fontFamily: "DM Sans", fontSize: "10.5px", fontWeight: 600, color: "#3E6BA8",
              background: "var(--gmb-primary-soft, #EAF1F8)", borderRadius: "7px",
            },
            // One block per workspace when they disagree on the rule —
            // visually separated so "How you earn points" never reads as if
            // it applies to the whole combined tile.
            "& .mi-ws-block": {
              marginTop: "10px", paddingTop: "10px", borderTop: "1px solid #F0F2F6",
            },
            "& .mi-ws-block:first-of-type": { marginTop: "9px", paddingTop: 0, borderTop: "none" },
            "& .mi-ws-name": {
              fontFamily: "DM Sans", fontSize: "10.5px", fontWeight: 700, color: "#101828",
            },
            "& .mi-ws-block .mi-row": { paddingTop: "6px" },
            "& .mi-row": { display: "flex", alignItems: "flex-start", gap: "9px", paddingTop: "10px" },
            "& .mi-ico": { width: 15, flex: "none", color: "#98A2B3", display: "grid", placeItems: "center", marginTop: "1px" },
            "& .mi-lbl": {
              display: "block", fontFamily: "DM Sans", fontSize: "10px", fontWeight: 600,
              letterSpacing: "0.04em", textTransform: "uppercase", color: "#98A2B3",
            },
            "& .mi-val": {
              display: "block", fontFamily: "DM Sans", fontSize: "12.5px", color: "#344054",
              marginTop: "2px", lineHeight: 1.45,
            },
            "& .mi-val b": { color: "#101828", fontWeight: 600 },
            "& .mi-pts": { color: "var(--gmb-primary, #2C6BA1)", fontWeight: 600 },
            // The one thing players get wrong gets its own warm strip.
            "& .mi-req": {
              display: "flex", alignItems: "flex-start", gap: "9px", marginTop: "10px",
              padding: "9px 10px", background: "#FEF3E6", border: "1px solid #F5D9B8", borderRadius: "9px",
            },
            "& .mi-req .mi-ico": { color: "#B54708" },
            "& .mi-req .mi-lbl": { color: "#B54708", fontWeight: 700 },
            "& .mi-req .mi-val": { color: "#7A4A12", fontSize: "12px" },
            "& .mi-note": {
              marginTop: "10px", paddingTop: "9px", borderTop: "1px solid #F0F2F6",
              fontFamily: "DM Sans", fontSize: "11.5px", color: "#667085", lineHeight: 1.45,
            },
          },
        },
      }}
    >
      <button
        type="button"
        aria-label={`How ${kpi.title || kpi.name} works`}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 15, height: 15, padding: 0, marginLeft: 4, border: "none", background: "none",
          color: darkMode ? "#8B949E" : "#C4CDD8", display: "grid", placeItems: "center",
          cursor: "pointer", flex: "none", lineHeight: 0,
        }}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <path d="M12 16v-4M12 8h.01" />
        </svg>
      </button>
    </Tooltip>
  );
}
